import React, { useEffect, useState, useMemo } from 'react'
import AddEditForm from '../../../sharedComponents/AddEditForm'
import { PencilIcon, TrashIcon, PlusIcon, CheckCircleIcon, ClockIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageWeightPriceList from './useManageWeightPriceList';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '600px',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const items = [
  {
    title: 'URL สินค้า',
    name: 'url',
    type: 'text',
    placeholder: 'วาง URL สินค้าที่ต้องการตั้งราคาน้ำหนักล่วงหน้า'
  },
  {
    title: 'ราคาน้ำหนัก (THB)',
    name: 'weightPrice',
    type: 'number',
    step: '0.01',
    placeholder: 'ราคาน้ำหนักต่อกิโลกรัม'
  },
  {
    title: 'หมายเหตุ',
    name: 'note',
    type: 'textarea',
    placeholder: 'หมายเหตุเพิ่มเติม (ไม่บังคับ)'
  },
];

const initialValue = {
  id: '',
  url: '',
  weightPrice: '',
  note: '',
}

const WeightPriceListSetting = () => {

  const [mode, setMode] = useState('register')
  const [modalTitle, setModalTitle] = useState('เพิ่มราคาน้ำหนักล่วงหน้า')
  const [filterStatus, setFilterStatus] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [weightPriceItem, setWeightPriceItem] = useState(initialValue);

  const {
    weightPriceItems,
    isLoading,
    error,
    addWeightPriceItem,
    editWeightPriceItem,
    deleteWeightPriceItem,
    refreshWeightPriceItems
  } = useManageWeightPriceList();

  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setWeightPriceItem({
      ...weightPriceItem, 
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const openForm = async (isEdit, id) => {
    if(isEdit){
      const itemToEdit = weightPriceItems.find(item => item.id === id);
      if(itemToEdit) {
        setWeightPriceItem(itemToEdit);
        setModalTitle('แก้ไขราคาน้ำหนักล่วงหน้า');
        setMode('edit');
      }

    }else{
      setWeightPriceItem(initialValue)
      setMode('register')
      setModalTitle('เพิ่มราคาน้ำหนักล่วงหน้า')
    }
    setIsFormOpen(true);
  };

  const closeForm = () =>{
    setIsFormOpen(false);
  }

  const openConfirm = async (id) => {
    const itemToDelete = weightPriceItems.find(item => item.id === id);
    if(itemToDelete) {
      setWeightPriceItem(itemToDelete);
      setIsConfirmOpen(true)
    }
  };

  const closeConfirm = () =>{
    setIsConfirmOpen(false)
  }

  const handleRegisterWeightPrice = async () => {
    try {
        const returnResult = await addWeightPriceItem(weightPriceItem);
        if (returnResult) {
          toast.success('เพิ่มราคาน้ำหนักสำเร็จ!');
          closeForm();
        } else {
          toast.error(error);
        }
    } catch (err) {
      toast.error('เกิดข้อผิดพลาดในการเพิ่มราคาน้ำหนัก');
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleUpdateWeightPrice = async () => {
    try {
      await editWeightPriceItem(weightPriceItem.id, weightPriceItem);
      toast.success('อัพเดทราคาน้ำหนักสำเร็จ!');
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteWeightPrice = async (id) => {
    try {
      await deleteWeightPriceItem(id);
      toast.success('ลบราคาน้ำหนักสำเร็จ!');
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    refreshWeightPriceItems(status);
  };

  useEffect(() => {
    refreshWeightPriceItems();
  }, []);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return weightPriceItems;
    }
    const searchLower = searchTerm.toLowerCase();
    return weightPriceItems.filter(item => 
      item.url?.toLowerCase().includes(searchLower) ||
      item.note?.toLowerCase().includes(searchLower) ||
      item.weightPrice?.toString().includes(searchLower) ||
      item.admin?.nickname?.toLowerCase().includes(searchLower)
    );
  }, [weightPriceItems, searchTerm]);

  // Count items by status (using filtered items for accurate counts when searching)
  const pendingCount = filteredItems.filter(item => !item.isApplied).length;
  const appliedCount = filteredItems.filter(item => item.isApplied).length;

  const handleSearchClear = () => {
    setSearchTerm('');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className='text-5xl'>ตั้งราคาน้ำหนักล่วงหน้า</h2>
        <button
          onClick={() => openForm(false)}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          เพิ่มรายการใหม่
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="mb-4 space-y-3">
        {/* Search Input */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="ค้นหาโดย URL, หมายเหตุ, ราคา, หรือชื่อผู้เพิ่ม..."
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
          {searchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={handleSearchClear}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => handleFilterChange('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ทั้งหมด ({weightPriceItems.length})
          </button>
          <button
            onClick={() => handleFilterChange('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              filterStatus === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            <ClockIcon className="h-4 w-4" />
            รอซื้อ ({pendingCount})
          </button>
          <button
            onClick={() => handleFilterChange('applied')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              filterStatus === 'applied'
                ? 'bg-green-600 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            <CheckCircleIcon className="h-4 w-4" />
            ซื้อแล้ว ({appliedCount})
          </button>
        </div>
      </div>

      {/* Loader */}
      {isLoading && (
          <div className="flex justify-center items-center h-60">
              <div className="loader"></div>
          </div>
      )}

      {/* Error Message */}
      {error && (
          <div className="my-4 p-4 bg-red-100 text-red-500 border border-red-400 rounded">
              An error occurred: {error}
          </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                URL สินค้า
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ราคาน้ำหนัก (THB)
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                สถานะ
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                จำนวนครั้งที่ใช้
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                หมายเหตุ
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ผู้เพิ่ม
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                การดำเนินการ
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems && filteredItems.length > 0 && filteredItems.map((item) => (
              <tr key={item.id} className={`hover:bg-gray-50 ${item.isApplied ? 'bg-green-50' : ''}`}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs">
                  <a 
                    href={item.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline truncate block"
                    title={item.url}
                  >
                    {item.url?.length > 50 ? item.url.substring(0, 50) + '...' : item.url}
                  </a>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                  {item.weightPrice ? `฿${parseFloat(item.weightPrice).toLocaleString()}` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${
                    item.isApplied 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.isApplied ? (
                      <>
                        <CheckCircleIcon className="h-3 w-3" />
                        ใช้งานแล้ว
                      </>
                    ) : (
                      <>
                        <ClockIcon className="h-3 w-3" />
                        รอใช้งาน
                      </>
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.applyCount || 0} ครั้ง
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                  {item.note || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.admin?.nickname || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openForm(true, item.id)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                      title="แก้ไข"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openConfirm(item.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded"
                      title="ลบ"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(!filteredItems || filteredItems.length === 0) && (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  {searchTerm ? 'ไม่พบข้อมูลที่ค้นหา' : 'ไม่มีข้อมูลราคาน้ำหนักล่วงหน้า'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm 
          items={items} 
          name={modalTitle} 
          data={weightPriceItem} 
          mode={mode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleRegister={handleRegisterWeightPrice}
          handleUpdate={handleUpdateWeightPrice}
          stateValue={weightPriceItem}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          titleText={`ยืนยันลบราคาน้ำหนัก: ${weightPriceItem.weightPrice ? `฿${parseFloat(weightPriceItem.weightPrice).toLocaleString()}` : ''}`}
          data={weightPriceItem}
          closeConfirm={closeConfirm}
          confirmFunction={() => handleDeleteWeightPrice(weightPriceItem.id)}
        />
      </Modal>
    </div>
  );
};

export default WeightPriceListSetting;

